import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import './PublicScanReportPage.scss';
import {
  confirmPublicScan,
  getPublicScanReport,
  getPublicScanStatus,
} from './services/PublicScanService';
import { PUBLIC_SCAN, SIGNUP } from '../../shared/routes/path';
import ScanProgress from './components/scan-progress/ScanProgress';
import {
  ReportCompetitiveRanking,
  ReportCompetitorMatrix,
  ReportCover,
  ReportPlatformPerformance,
  ReportPromptAnalysis,
  ReportShareOfVoice,
  ReportSourceDomains,
  ReportSourcePages,
  ReportSummary,
} from '../../shared/components/report';
import { useReportPdfDownload } from '../../shared/components/report/pdf/UseReportPdfDownload';
import { buildBrandColors } from '../../shared/components/report/brandColors';
import { brandLabel } from '../../shared/components/report/brandLabel';

const STATUS_POLL_MS = 3_000;

const PublicScanReportPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token = '' } = useParams();

  // Link z maila niesie `confirmToken`, a raport żyje pod `reportToken`. Pierwsze wejście wymienia
  // jeden na drugi i podmienia URL, więc odświeżenie strony trafia już prosto w raport.
  const [reportToken, setReportToken] = useState<string | null>(null);

  // Globalny `body { overflow: hidden }` z index.scss — bez tego raport się nie scrolluje.
  useEffect(() => {
    document.body.classList.add('visible');
  }, []);

  useEffect(() => {
    let cancelled = false;

    confirmPublicScan(token)
      .then((response) => {
        if (cancelled) return;
        setReportToken(response.reportToken);
        navigate(`${PUBLIC_SCAN}/${response.reportToken}`, { replace: true });
      })
      .catch(() => {
        // 404 znaczy, że w URL-u jest już `reportToken` (odświeżenie strony raportu).
        if (!cancelled) setReportToken(token);
      });

    return () => {
      cancelled = true;
    };
  }, [token, navigate]);

  const { data: status, isError: isStatusError } = useQuery({
    queryKey: ['public-scan-status', reportToken],
    queryFn: () => getPublicScanStatus(reportToken as string),
    enabled: !!reportToken,
    refetchInterval: (query) =>
      query.state.data?.status === 'READY' || query.state.data?.status === 'FAILED'
        ? false
        : STATUS_POLL_MS,
  });

  const { data: report } = useQuery({
    queryKey: ['public-scan-report', reportToken],
    queryFn: () => getPublicScanReport(reportToken as string),
    enabled: !!reportToken && status?.status === 'READY',
    staleTime: Infinity,
  });

  const {
    download: downloadPdf,
    isDownloading,
    rankingChartRef,
    sovChartRef,
  } = useReportPdfDownload(report);

  // Jedna mapa kolorów na cały raport — kolejność Share of Voice jest kanoniczna.
  const brandColors = useMemo(
    () =>
      buildBrandColors(
        (report?.shareOfVoice ?? []).map((entry) => ({
          brand: brandLabel(entry.brand),
          isTracked: entry.isTracked,
        }))
      ),
    [report]
  );

  if (isStatusError || status?.status === 'FAILED') {
    return (
      <div className="public-scan-report-page">
        <div className="public-scan-report public-scan-report--message">
          <h1>{t('scanFailedTitle')}</h1>
          <p>{t('scanFailedDesc')}</p>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="public-scan-report-page">
        <div className="public-scan-report public-scan-report--message">
          <ScanProgress status={status} />
        </div>
      </div>
    );
  }

  return (
    <div className="public-scan-report-page">
      <div className="public-scan-report">
        <ReportCover
          brand={report.brand}
          generatedAt={report.generatedAt}
          promptsAnalyzed={report.promptsAnalyzed}
          models={report.visibilityPerModel.map((entry) => entry.model)}
          onDownloadPdf={downloadPdf}
          isDownloading={isDownloading}
        />
        <ReportSummary brand={report.brand} score={report.score} />
        <ReportPlatformPerformance models={report.visibilityPerModel} />
        <ReportCompetitiveRanking
          brands={report.brandVisibility}
          brandColors={brandColors}
          chartRef={rankingChartRef}
        />
        <ReportShareOfVoice
          brands={report.shareOfVoice}
          brandColors={brandColors}
          chartRef={sovChartRef}
        />
        <ReportCompetitorMatrix matrix={report.competitorMatrix} brandColors={brandColors} />
        <ReportSourceDomains sources={report.sources} />
        <ReportSourcePages pages={report.sources.topPages} />
        <ReportPromptAnalysis locked signUpHref={SIGNUP} />
      </div>
    </div>
  );
};

export default PublicScanReportPage;
